import {
    type UIEvent,
    useRef,
    ReactElement,
    useCallback,
    useEffect,
    useMemo,
} from 'react'
import {
    MaterialReactTable,
    useMaterialReactTable,
    type MRT_ColumnDef,
    MRT_RowData,
    MRT_RowVirtualizer,
    MRT_RowSelectionState,
} from 'material-react-table'
import { Box, LinearProgress, Typography } from '@mui/material'
import {
    FetchNextPageOptions,
    InfiniteData,
    InfiniteQueryObserverResult,
} from '@tanstack/react-query'
import { canSelect } from '../../utils/EntityUtils'
import { useTranslation } from 'react-i18next'
import React from 'react'
import { useAppDispatch, useAppSelector } from '../../redux/hook'
import {
    selectEntityViewScrollPosition,
    setScrollPosition,
    setTableColumnSizing,
} from '../../redux/slices/entitySlice'

interface IMRTTable<DataType extends MRT_RowData, ResponseType> {
    columns: MRT_ColumnDef<DataType>[]
    data: DataType[]
    totalDBRowCount: number
    fetchNextPage: (
        options?: FetchNextPageOptions | undefined,
    ) => Promise<
        InfiniteQueryObserverResult<InfiniteData<ResponseType, unknown>, Error>
    >
    isError: boolean
    isFetching: boolean
    isLoading: boolean
    setRowSelection: React.Dispatch<React.SetStateAction<MRT_RowSelectionState>>
    rowSelection: MRT_RowSelectionState
    hasRowSelection: boolean
    hasRowSelectionForEach?: boolean
    selectionColumnId?: string

    hasNextPage?: boolean
    getRowBackground?: (row: DataType) => string
    rowHeight?: number

    columnVisibility?: Record<string, boolean>
    setColumnVisibility?: React.Dispatch<
        React.SetStateAction<Record<string, boolean>>
    >
    columnOrder?: string[]
    height?: string
    sortParams?: {
        orderAttribute: string
        orderBy: 'ASC' | 'DESC'
    }
    setSortParams?: React.Dispatch<
        React.SetStateAction<{
            orderAttribute: string
            orderBy: 'ASC' | 'DESC'
        }>
    >
    columnFilters?: {
        id: string
        value: any
    }[]
    setColumnFilters: React.Dispatch<
        React.SetStateAction<{ id: string; value: any }[]>
    >
    onRowClick?: (row: DataType) => void

    muiTablePaperProps?: any
    tableIdentifier?: string
    layoutMode?: 'grid' | 'grid-no-grow'
}

const DynamicTable = <DataType extends MRT_RowData, ResponseType>(
    props: IMRTTable<DataType, ResponseType>,
): ReactElement => {
    const {
        columns,
        data,
        totalDBRowCount,
        fetchNextPage,
        isError,
        isFetching,
        isLoading,
        setRowSelection,
        rowSelection,
        hasRowSelection,
        hasRowSelectionForEach,
        selectionColumnId = 'mrt-row-select',
        columnVisibility = {},
        setColumnVisibility,
        columnOrder,
        height,
        sortParams,
        setSortParams,
        columnFilters,
        setColumnFilters,
        onRowClick,
        hasNextPage,
        getRowBackground,
        rowHeight,
        muiTablePaperProps,
        tableIdentifier,
        layoutMode = 'grid-no-grow',
    } = props
    const { t } = useTranslation()
    const tableContainerRef = useRef<HTMLDivElement>(null)
    const rowVirtualizerInstanceRef = useRef<MRT_RowVirtualizer>(null)
    const dispatch = useAppDispatch()
    const scrollPosition = useAppSelector(selectEntityViewScrollPosition)
    const hasRestoredScroll = useRef(false)

    const totalFetched = data?.length ?? 0

    useEffect(() => {
        // only try to restore scroll on the first render with data and a scroll position
        if (
            data.length > 0 &&
            scrollPosition > 0 &&
            !hasRestoredScroll.current
        ) {
            setTimeout(() => {
                if (hasRestoredScroll.current) return

                if (rowVirtualizerInstanceRef.current) {
                    rowVirtualizerInstanceRef.current.scrollToOffset(
                        scrollPosition,
                    )
                } else if (tableContainerRef.current) {
                    tableContainerRef.current.scrollTop = scrollPosition
                }
                // set flag to true so it doesn't run again
                hasRestoredScroll.current = true
            }, 100)
        }
    }, [data.length, scrollPosition, dispatch, rowVirtualizerInstanceRef])

    const allTableColumnSizing = useAppSelector(
        (state) => state.entity.allTableColumnSizing,
    )

    const columnSizing = useMemo(
        () =>
            allTableColumnSizing.find((item) => item.id === tableIdentifier)
                ?.sizing || {},
        [allTableColumnSizing, tableIdentifier],
    )

    const handleColumnSizingChange = useCallback(
        (updater: any) => {
            if (!tableIdentifier) return

            const newSizing =
                typeof updater === 'function' ? updater(columnSizing) : updater

            dispatch(
                setTableColumnSizing({
                    id: tableIdentifier,
                    sizing: newSizing,
                }),
            )
        },
        [dispatch, tableIdentifier, columnSizing],
    )

    const initialStickyColumns: Record<string, number> = useMemo(
        () => ({
            [selectionColumnId]: 50,
            viewid: 40,
            editid: 40,
            shipIcon: 40,
        }),
        [selectionColumnId],
    )

    const [stickyColumns, setStickyColumns] =
        React.useState(initialStickyColumns)

    useEffect(() => {
        const newStickyColumns = { ...initialStickyColumns }

        if (!hasRowSelection && selectionColumnId === 'mrt-row-select') {
            delete newStickyColumns['mrt-row-select']
        }
        if (!columns.find((col) => col.id === 'viewid')) {
            delete newStickyColumns.viewid
        }
        if (!columns.find((col) => col.id === 'shipIcon')) {
            delete newStickyColumns.shipIcon
        }
        if (!columns.find((col) => col.id === 'editid')) {
            delete newStickyColumns.editid
        }
        setStickyColumns(newStickyColumns)
    }, [columns, hasRowSelection, selectionColumnId, initialStickyColumns])

    const fetchMoreOnBottomReached = useCallback(
        (containerRefElement?: HTMLDivElement | null) => {
            if (containerRefElement) {
                const { scrollHeight, scrollTop, clientHeight } =
                    containerRefElement
                const shouldFetch =
                    hasNextPage !== undefined
                        ? hasNextPage
                        : totalFetched < totalDBRowCount
                if (
                    scrollHeight - scrollTop - clientHeight < 400 &&
                    !isFetching &&
                    shouldFetch
                ) {
                    fetchNextPage()
                }
            }
        },
        [fetchNextPage, isFetching, totalFetched, totalDBRowCount, hasNextPage],
    )

    useEffect(() => {
        fetchMoreOnBottomReached(tableContainerRef.current)
    }, [fetchMoreOnBottomReached])

    const table = useMaterialReactTable({
        columns,
        data,
        renderEmptyRowsFallback: () => (
            <Typography
                sx={{
                    position: 'fixed',
                    top: '300px',
                    left: 'calc(50%)',
                }}
                className="noRecordsToDisplay"
            >
                {t('noRecordsToDisplay')}
            </Typography>
        ),
        enablePagination: false,
        enableRowVirtualization: true,
        rowVirtualizerInstanceRef,
        rowVirtualizerOptions: {
            overscan: 10,
            estimateSize: () => rowHeight ?? 35,
        },
        enableColumnActions: false,
        enableTopToolbar: false,
        enableRowNumbers: false,
        enableBottomToolbar: false,
        enableStickyHeader: true,
        enableTableHead: true,
        enableRowSelection: hasRowSelection
            ? (row) =>
                  canSelect(row.original) ? true : !!hasRowSelectionForEach
            : false,
        enableSelectAll: false,

        layoutMode,
        enableColumnResizing: true,
        manualSorting: true,
        onSortingChange: (updater) => {
            const sortingState =
                typeof updater === 'function'
                    ? updater(table.getState().sorting)
                    : updater

            if (sortingState.length > 0) {
                const { id, desc } = sortingState[0]
                setSortParams?.({
                    orderAttribute: id,
                    orderBy: desc ? 'DESC' : 'ASC',
                })
            } else {
                // reset if no sorting
                setSortParams?.({
                    orderAttribute: '',
                    orderBy: 'ASC',
                })
            }
            ;(document.activeElement as HTMLElement)?.blur()
            // scroll to top after sorting
            try {
                rowVirtualizerInstanceRef.current?.scrollToIndex?.(0)
            } catch (err) {
                console.error(err)
            }
        },
        enableColumnFilters: true,
        manualFiltering: true,
        columnFilterDisplayMode: 'popover',
        onColumnFiltersChange: setColumnFilters,
        onRowSelectionChange: setRowSelection,
        onColumnSizingChange: handleColumnSizingChange,
        muiSelectCheckboxProps: ({ row }) => {
            return {
                sx: {
                    display: canSelect(row.original)
                        ? ''
                        : hasRowSelectionForEach
                          ? ''
                          : 'none',
                },
            }
        },
        muiTableBodyProps: {
            sx: {
                '&:has(.noRecordsToDisplay)': {
                    position: 'unset',
                },
            },
        },
        muiTableBodyRowProps: ({ row }) => {
            return {
                id: `row-${row.id}`,
                sx: {
                    height: rowHeight ? 'auto' : 35,
                    minHeight: rowHeight ? `${rowHeight}px` : 35,
                    cursor: onRowClick ? 'pointer' : undefined,
                },
                onClick: onRowClick
                    ? () => onRowClick(row.original as DataType)
                    : undefined,
            }
        },
        muiTableHeadProps: {
            sx: {
                th: {
                    borderBottom: '1px solid #F5F7F9',
                    background: '#F5FBFF',
                    marginRight: '1px',
                },
            },
        },
        muiTablePaperProps: {
            sx: {
                borderBottomLeftRadius: '5px',
                borderBottomRightRadius: '5px',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                ...muiTablePaperProps?.sx,
            },
        },
        muiTableHeadCellProps: ({ column }) => {
            const filterValue = column.getFilterValue() as any[]
            return {
                id: `column-header-${column.id}`,
                sx: {
                    '& .Mui-TableHeadCell-ResizeHandle-Wrapper .Mui-TableHeadCell-ResizeHandle-Divider':
                        {
                            backgroundColor: '#ccc',
                            borderWidth: '1.5px',
                            borderRadius: '1px',
                            marginTop: '3px',
                        },
                    '&:first-of-type': {
                        borderLeft: '1px solid #F5F7F9',
                    },
                    '& .Mui-TableHeadCell-Content-Labels': {
                        '.MuiTableSortLabel-directionAsc': {
                            paddingTop: '3px',
                        },
                        button: {
                            paddingTop: '14px',
                        },
                    },
                    backgroundColor: filterValue?.length
                        ? '#ccc !important'
                        : '',
                    ...getStickySx(column.id, true),
                },
            }
        },
        muiTableBodyCellProps: ({ row, column }) => ({
            id: `row-${row.id}-cell-${column.id}`,
            sx: {
                backgroundColor: getRowBackground
                    ? getRowBackground(row.original as DataType)
                    : '#ffffff',
                borderBottom: '1px solid #F5F7F9',
                borderTop: '1px solid #F5F7F9',
                '&:first-of-type': {
                    borderLeft: '1px solid #F5F7F9',
                },
                '&:last-child': {
                    borderRight: '1px solid #F5F7F9',
                },
                // Column-specific styles
                ...(column.id === 'viewid' && {
                    textAlign: 'center',
                    padding: '0px',
                }),
                ...(column.id === 'editid' && {
                    textAlign: 'center',
                    padding: '0px',
                }),
                ...(column.id === 'shipIcon' && {
                    alignItems: 'center',
                    justifyContent: 'center',
                    textAlign: 'center',
                    padding: '0px',
                }),
                ...(column.id === 'mrt-row-select' && {
                    alignItems: 'center',
                    justifyContent: 'center',
                }),
                ...getStickySx(column.id, false),
            },
        }),
        muiTableContainerProps: {
            ref: tableContainerRef,
            sx: {
                flex: '1 1 auto',
                overflow: 'auto',
                minHeight: '100px',
                ...(tableIdentifier?.startsWith('alerts-')
                    ? {
                          width: '100%',
                      }
                    : {}),
            },
            onScroll: (event: UIEvent<HTMLDivElement>) => {
                const { scrollTop } = event.target as HTMLDivElement
                if (
                    !hasRestoredScroll.current &&
                    scrollPosition > 0 &&
                    scrollTop === 0
                ) {
                    return
                }
                dispatch(setScrollPosition(scrollTop))
                fetchMoreOnBottomReached(event.target as HTMLDivElement)
            },
        },
        displayColumnDefOptions: {
            'mrt-row-select': {
                size: 20,
                grow: false,
                header: '',
            },
        },
        icons: {
            ArrowDownwardIcon: (props: any) => {
                return (
                    <Box
                        {...props}
                        sx={{
                            fontSize: '14px !important',
                            height: '16px',
                            display: 'flex',
                            '&.MuiTableSortLabel-iconDirectionAsc': {
                                alignItems: 'flex-end !important',
                            },
                            '&.MuiTableSortLabel-iconDirectionDesc': {
                                alignItems: 'flex-start !important',
                            },
                        }}
                    >
                        <Box>▼</Box>
                    </Box>
                )
            },
        },
        defaultColumn: {
            minSize: 40,
            maxSize: 9001,
            size: 150,
            grow: true,
        },

        state: {
            isLoading,
            showAlertBanner: isError,
            showProgressBars: isFetching,
            rowSelection,
            density: 'compact',
            columnVisibility,
            columnOrder,
            sorting: sortParams
                ? [
                      {
                          id: sortParams.orderAttribute,
                          desc: sortParams.orderBy === 'DESC',
                      },
                  ]
                : [],
            columnFilters,
            showColumnFilters: true,
            columnSizing,
        },
        onColumnVisibilityChange: setColumnVisibility,
        renderToolbarInternalActions: () =>
            isFetching ? <LinearProgress sx={{ mt: 1 }} /> : null,
        localization: {
            filteringByColumn: t(''),
        },
    })

    const getStickySx = (columnId: string, isHeader: boolean) => {
        let left = 0
        let isSticky = false
        // Check if current column is the primary selection column or marked as sticky
        if (
            columnId === selectionColumnId ||
            Object.hasOwn(stickyColumns, columnId)
        ) {
            isSticky = true
        }

        if (!isSticky) {
            return {}
        }

        for (const key in stickyColumns) {
            if (key === columnId) {
                break
            }
            const column = table.getColumn(key)
            const columnSize = column?.getSize() || stickyColumns[key]
            left += columnSize
        }

        return {
            position: 'sticky',
            left: `${left}px`,
            zIndex: isHeader ? 3 : 2,
            ...(selectionColumnId != 'customSelect'
                ? { background: isHeader ? '#F5FBFF' : '#FFF' }
                : {}),
        }
    }

    return (
        <>
            <Box sx={{ height: `calc(${height} - 4px)` }}>
                {/*This -4x is to accommodate below LinearProgress */}
                <MaterialReactTable table={table} />
            </Box>
            {isFetching && <LinearProgress />}
        </>
    )
}

export default DynamicTable
